import { useQuery } from '@apollo/client';
import { CloudSync, DevicesOther } from '@mui/icons-material';
import { Box, Button, Tooltip } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '~/contexts';
import { DataItemCellSelectListDocument } from './DataItemCellSelect.generated';
import {
  DataItemCellSelectModal,
  DataItemCellSelectProperty,
  DeviceValuesPreview,
} from './components';
import {
  useCachedDataItem,
  type DataItemData,
  type SelectedCellParams,
  type SelectedColumnParams,
} from './lib';

export interface DataItemCellSelectProps {
  dataItemId: number | undefined;
  dataItemKey: string | undefined;
  deviceDataSourceId: number | undefined;
  disabled?: boolean;
  onChange: (selected: SelectedCellParams | SelectedColumnParams | undefined) => void;
  onPreviewDeviceChange: (deviceId: string) => void;
  open: boolean;
  previewDeviceId: string;
  /** What the canvas is currently showing for this item under the previewed device. */
  previewValue?: string;
  setOpen: (open: boolean) => void;
}

export const DataItemCellSelect = ({
  dataItemId,
  dataItemKey,
  deviceDataSourceId,
  disabled,
  onChange,
  onPreviewDeviceChange,
  open,
  previewDeviceId,
  previewValue,
  setOpen,
}: DataItemCellSelectProps) => {
  const { t } = useTranslation();
  const dataItem = useCachedDataItem(dataItemId);
  const [deviceAware, setDeviceAware] = useState(Boolean(deviceDataSourceId));

  const { currentNetwork } = useAppContext();

  const { data: sourcesQuery, loading } = useQuery(DataItemCellSelectListDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const dataSources = useMemo(() => sourcesQuery?.network?.allDataSources || [], [sourcesQuery]);

  // Only a source whose rows are keyed by device ID can be matched against a device.
  const matchableSources = useMemo(
    () => dataSources.filter((x) => Boolean(x.deviceKey)),
    [dataSources],
  );

  const dataSource = useMemo(
    () => dataSources.find((x) => x.id === deviceDataSourceId),
    [dataSources, deviceDataSourceId],
  );

  const data: DataItemData = dataItem?.data || {};
  const connected = Boolean(dataItemId || deviceDataSourceId);

  const openIn = (asDeviceAware: boolean) => {
    setDeviceAware(asDeviceAware);
    setOpen(true);
  };

  return (
    <>
      {connected ? (
        <>
          <Button
            color="error"
            disabled={disabled}
            onClick={() => onChange(undefined)}
            startIcon={<CloudSync />}
            variant="contained"
          >
            {t('components.data_item_cell_select.fields.disconnect')}
          </Button>
          <Box display="flex" flexDirection="column" gap={3} sx={{ paddingY: 2 }}>
            <DataItemCellSelectProperty
              onClick={() => openIn(Boolean(deviceDataSourceId))}
              title={t('components.data_item_cell_select.fields.data_source_label')}
              value={deviceDataSourceId ? dataSource?.name : dataItem?.source.name}
            />
            {deviceDataSourceId ? (
              <DataItemCellSelectProperty
                title={t('components.data_item_cell_select.fields.row_label')}
                value={t('components.data_item_cell_select.fields.row_matches_device', {
                  column: dataSource?.deviceKey ?? '',
                })}
              />
            ) : null}
            {!deviceDataSourceId && dataItem?.source.foreignIdName ? (
              <DataItemCellSelectProperty
                title={t('components.data_item_cell_select.fields.unique_id_label')}
                value={data[dataItem.source.foreignIdName]}
              />
            ) : null}
            <DataItemCellSelectProperty
              onClick={deviceDataSourceId ? () => openIn(true) : undefined}
              title={t('components.data_item_cell_select.fields.column_label')}
              value={dataItemKey}
            />
            {!deviceDataSourceId ? (
              <DataItemCellSelectProperty
                onClick={() => openIn(false)}
                title={t('components.data_item_cell_select.fields.cell_label')}
                value={dataItemKey ? data[dataItemKey] : ''}
              />
            ) : null}
            {deviceDataSourceId && dataItemKey ? (
              <DeviceValuesPreview
                onPreviewDeviceChange={onPreviewDeviceChange}
                previewDeviceId={previewDeviceId}
                previewValue={previewValue}
              />
            ) : null}
          </Box>
        </>
      ) : (
        <Box display="flex" flexDirection="column" gap={1}>
          <Button
            disabled={disabled}
            onClick={() => openIn(false)}
            startIcon={<CloudSync />}
            variant="contained"
          >
            {t('components.data_item_cell_select.fields.connect')}
          </Button>
          <Tooltip
            title={
              matchableSources.length
                ? ''
                : t('components.data_item_cell_select.fields.connect_device_unavailable')
            }
          >
            <span>
              <Button
                disabled={disabled || !matchableSources.length}
                fullWidth
                onClick={() => openIn(true)}
                startIcon={<DevicesOther />}
                variant="outlined"
              >
                {t('components.data_item_cell_select.fields.connect_device')}
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {open && !loading ? (
        <DataItemCellSelectModal
          dataItem={dataItem}
          dataItemKey={dataItemKey}
          dataSourceId={deviceDataSourceId}
          dataSources={deviceAware ? matchableSources : dataSources}
          deviceAware={deviceAware}
          onChange={onChange}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
};
